import axios from "../plugins/axios";
import { getField, updateField } from "vuex-map-fields";
import { readableReason } from "@/helpers/humanText";

// Shape-email-origin submissions that the backend could not confidently match
// to an existing company are staged here for the broker to review before they
// become normal customer/submission records. This module is intentionally
// self-contained (its own namespace) rather than folded into `broker` /
// `brokerSubmission` so the temporary Review surface can be lifted out cleanly
// once email-origin intake is fully automated.
//
// S2R BE keys intake-review rows by `id` (both the REST list and the Pusher
// payloads use `id`), so that is the review item key everywhere.
const reviewRowId = (row) => row?.id || null;

const state = () => ({
  // Full list of pending items — only populated once the Review view fetches
  // it. Realtime events only mutate this array when it has been loaded.
  pendingItems: [],
  // Lightweight badge count. Fetched cheaply on broker login (nav) and kept in
  // sync by realtime events independent of whether the list is loaded.
  pendingCount: 0,
  // True once the list has been fetched at least once this session. Guards
  // realtime list upserts so events from before the broker opens Review don't
  // seed a phantom list that never gets reconciled against the server.
  listLoaded: false,
  loading: false,
  loadingCount: false,
  error: null,
});

const mutations = {
  updateField,
  setState(state, { key, value }) {
    state[key] = value;
  },

  setPendingItems(state, items) {
    state.pendingItems = Array.isArray(items) ? items : [];
    state.listLoaded = true;
  },

  setPendingCount(state, count) {
    const n = Number(count);
    state.pendingCount = Number.isFinite(n) && n > 0 ? n : 0;
  },

  // Insert or merge a single pending item. No-op when the list hasn't been
  // loaded yet (see `listLoaded`).
  upsertPendingItem(state, item) {
    if (!item || !state.listLoaded) return;
    const rowId = reviewRowId(item);
    if (!rowId) return;
    const idx = state.pendingItems.findIndex((existing) => reviewRowId(existing) === rowId);
    if (idx > -1) {
      // Vue 2 reactivity: replace via splice, not index assignment.
      state.pendingItems.splice(idx, 1, { ...state.pendingItems[idx], ...item });
    } else {
      state.pendingItems.unshift({ ...item });
    }
  },

  removePendingItem(state, id) {
    if (!id) return;
    for (let i = state.pendingItems.length - 1; i >= 0; i--) {
      if (reviewRowId(state.pendingItems[i]) === id) {
        state.pendingItems.splice(i, 1);
      }
    }
  },

  setLoading(state, value) {
    state.loading = Boolean(value);
  },

  setLoadingCount(state, value) {
    state.loadingCount = Boolean(value);
  },

  setError(state, value) {
    state.error = value || null;
  },
};

const actions = {
  // Fetch the full pending-review list (used by the Review view).
  async fetchPendingReviewItems({ commit }) {
    commit("setLoading", true);
    commit("setError", null);
    try {
      const { data } = await axios.get("/brokers/intake-review");
      if (!data?.success) {
        commit("setError", readableReason(data?.message, "Unable to load review items."));
        return [];
      }
      const items = data.items || data.reviewItems || [];
      commit("setPendingItems", items);
      // Keep the badge count authoritative with the list length unless the API
      // returns an explicit count.
      commit("setPendingCount", typeof data.count === "number" ? data.count : items.length);
      return items;
    } catch (e) {
      console.warn("[brokerReview/fetchPendingReviewItems] error:", e);
      commit("setError", readableReason(e?.response?.data?.message, "Unable to load review items."));
      return [];
    } finally {
      commit("setLoading", false);
    }
  },

  // Cheap badge count — safe to call on every broker login / nav mount.
  async fetchPendingReviewCount({ commit }) {
    commit("setLoadingCount", true);
    try {
      const { data } = await axios.get("/brokers/intake-review/count", { withoutLoading: true });
      if (data?.success) {
        commit("setPendingCount", typeof data.count === "number" ? data.count : 0);
      }
      return data?.count ?? 0;
    } catch (e) {
      console.warn("[brokerReview/fetchPendingReviewCount] error:", e);
      return 0;
    } finally {
      commit("setLoadingCount", false);
    }
  },

  // Broker confirms the staged item IS the designated candidate company. The
  // backend promotes/merges the submission into that company's normal records.
  // The list row is removed locally for immediate feedback; the badge count is
  // only touched when the response carries an authoritative `count` — otherwise
  // the `pendingReviewCountChanged` event is the source of truth.
  async confirmPendingReviewMatch({ commit }, { reviewId }) {
    if (!reviewId) {
      return { success: false, message: "Missing review item id." };
    }
    try {
      const { data } = await axios.post(`/brokers/intake-review/${reviewId}/confirm-match`);
      if (data?.success) {
        commit("removePendingItem", reviewId);
        if (typeof data.count === "number") commit("setPendingCount", data.count);
      }
      return data;
    } catch (e) {
      console.warn("[brokerReview/confirmPendingReviewMatch] error:", e);
      return { success: false, message: readableReason(e?.response?.data?.message, "Unable to confirm match.") };
    }
  },

  // Broker marks the staged item as a brand-new company. The backend creates a
  // new customer/submission from the email-origin data.
  async markPendingReviewAsNew({ commit }, { reviewId }) {
    if (!reviewId) {
      return { success: false, message: "Missing review item id." };
    }
    try {
      const { data } = await axios.post(`/brokers/intake-review/${reviewId}/mark-new`);
      if (data?.success) {
        commit("removePendingItem", reviewId);
        if (typeof data.count === "number") commit("setPendingCount", data.count);
      }
      return data;
    } catch (e) {
      console.warn("[brokerReview/markPendingReviewAsNew] error:", e);
      return { success: false, message: readableReason(e?.response?.data?.message, "Unable to mark as new company.") };
    }
  },

  // ---- Realtime (Pusher) handlers ---------------------------------------
  // BE events: `pendingReviewCreated`, `pendingReviewUpdated`,
  // `pendingReviewCountChanged`. There is no separate "resolved" event — a
  // resolution arrives as a `pendingReviewUpdated` whose `state` is no longer
  // "pending", plus an authoritative `pendingReviewCountChanged` ({ count }).
  // The count is NEVER derived locally from these row events; the badge is
  // driven solely by `pendingReviewCountChanged`.
  pusherPendingReviewCreated({ commit }, item) {
    if (!item) return;
    commit("upsertPendingItem", item);
  },

  pusherPendingReviewUpdated({ commit }, item) {
    if (!item) return;
    const id = reviewRowId(item);
    // A resolved item (state !== "pending") leaves the list; a still-pending
    // item is upserted in place.
    if (item.state !== "pending") {
      if (id) commit("removePendingItem", id);
      return;
    }
    commit("upsertPendingItem", item);
  },

  pusherPendingReviewCountChanged({ commit }, payload) {
    const count = typeof payload === "number" ? payload : payload?.count;
    if (typeof count === "number") {
      commit("setPendingCount", count);
    }
  },
};

const getters = {
  getField,
  pendingItems: (state) => state.pendingItems,
  pendingCount: (state) => state.pendingCount,
  // Single source of truth the nav + Review route guard read to decide whether
  // the temporary Review surface should exist at all.
  hasPendingReview: (state) => state.pendingCount > 0,
  isLoadingReview: (state) => state.loading,
  reviewError: (state) => state.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
